import { SessionInfo } from './types';
import { AppError, ERROR_CODE } from '../util/AppError';
import {
  initAuth,
  login as kcLogin,
  localLogout as kcLocalLogout,
  logout as kcLogout,
  refreshToken as kcRefreshToken,
} from './keycloak';
import { invalidateUserCache } from './auth-user';
import { clearConfig } from './config';
import { clearRememberedSelection } from '../pages/addPatient/addPatient.utils';

const MIN_TOKEN_VALIDITY_SECONDS = 30;

// ── Realm detection ────────────────────────────────────────────────────────────

// TODO: restore when DNS migrated back to *.qarescan.com
// const REALM_HOSTNAME_PATTERN = /^([a-z0-9-]+)\.(dev|stg|prd)\.qarescan\.com$/i;
// const REALM_PROD_HOSTNAME_PATTERN = /^(?!dev\.|stg\.|prd\.)([a-z0-9-]+)\.qarescan\.com$/i;
// const orgUrlTemplate = (env: string) => `<organisation>.${env}.qarescan.com`;
// const ORG_URL_PROD_TEMPLATE = '<organisation>.qarescan.com';

// Temporary: matches <organisation>.qarescan.<platform>[.<env>].smartqare.cloud where
//   <platform> = platform | <build>-platform   (e.g. platform, pr-123-platform)
//   <build>    = [a-z0-9]+(-[a-z0-9]+)*
//   <env>      = dev | test | demo<N> | staging, or absent
//   <N>        = 1-2 digits, 0-99, leading zeros allowed (0, 01, 99)
const REALM_HOSTNAME_PATTERN =
  /^([a-z0-9-]+)\.qarescan\.((?:[a-z0-9]+(?:-[a-z0-9]+)*-)?platform)(?:\.(dev|test|demo\d{1,2}|staging))?\.smartqare\.cloud$/i;
// Temporary: matches <org>.qarescan.smartqare.cloud (production, no platform or env segment)
const REALM_PROD_HOSTNAME_PATTERN = /^([a-z0-9-]+)\.qarescan\.smartqare\.cloud$/i;
const orgUrlTemplate = (platform: string, env?: string): string =>
  `<organisation>.qarescan.${platform}${env ? `.${env}` : ''}.smartqare.cloud`;
const ORG_URL_PROD_TEMPLATE = '<organisation>.qarescan.smartqare.cloud';

export function getRealmFromHostname(hostname = window.location.hostname): string | null {
  const match = REALM_HOSTNAME_PATTERN.exec(hostname) ?? REALM_PROD_HOSTNAME_PATTERN.exec(hostname);
  return match ? match[1].toLowerCase() : null;
}

export function getOrgUrlPattern(hostname = window.location.hostname): string {
  const orgMatch = REALM_HOSTNAME_PATTERN.exec(hostname);
  if (orgMatch) return orgUrlTemplate(orgMatch[2].toLowerCase(), orgMatch[3]?.toLowerCase());
  if (REALM_PROD_HOSTNAME_PATTERN.test(hostname)) return ORG_URL_PROD_TEMPLATE;
  return orgUrlTemplate('platform', '<environment>');
}

// ── Session recovery ─────────────────────────────────────────────────────────

/**
 * Called on app start to restore an existing session.
 *
 * Calls the BFF /auth/me endpoint, which reads the HttpOnly session cookies
 * server-side and returns the authenticated user identity.
 *
 * Throws when the user is not authenticated anymore.
 */
export async function continueSession(): Promise<SessionInfo> {
  const {
    authenticated,
    given_name,
    family_name,
    organization_name,
    locale,
    userRole,
    email,
    preferred_username,
  } = await initAuth();

  if (!authenticated) throw new Error('no active session');

  return {
    username: [given_name, family_name].filter(Boolean).join(' '),
    organizationName: organization_name ?? undefined,
    organizationLanguage: locale ?? undefined,
    userRole: userRole ?? undefined,
    loginHint: email ?? preferred_username ?? undefined,
  };
}

// ── Login ──────────────────────────────────────────────────────────────────────

/**
 * Redirect the browser to the Keycloak login flow (PKCE).
 *
 * `realm` selects the organization realm, `loginHint` pre-fills the username
 * when supported, and `prompt='login'` forces the Keycloak login screen.
 */
export function login(realm?: string, loginHint?: string, prompt?: 'login'): void {
  kcLogin({ realm, loginHint, prompt });
}

// ── Refresh ────────────────────────────────────────────────────────────────────

// Shared promise deduplicates concurrent refresh calls so that simultaneous
// 401 responses don't each trigger a separate /auth/refresh request —
// Keycloak's token rotation would reject all but the first, causing spurious
// ERR_BAD_REFRESHTOKEN errors during normal active use.
let pendingRefresh: Promise<void> | null = null;

/** Refresh the session via the BFF refresh-token flow. */
export async function refresh(): Promise<void> {
  if (pendingRefresh) return pendingRefresh;
  pendingRefresh = kcRefreshToken(MIN_TOKEN_VALIDITY_SECONDS).then((ok) => {
    if (!ok) {
      throw new AppError(ERROR_CODE.ERR_BAD_REFRESHTOKEN, 'Session expired - please log in again.');
    }
  });
  try {
    await pendingRefresh;
  } finally {
    pendingRefresh = null;
  }
}

// ── Logout ─────────────────────────────────────────────────────────────────────

/** Trigger full Keycloak logout via browser redirect. */
export function logout(): void {
  invalidateUserCache();
  clearConfig();
  void clearRememberedSelection();
  kcLogout();
}

/** Clear only the local BFF session cookies without leaving the app. */
export async function localLogout(): Promise<void> {
  invalidateUserCache();
  clearConfig();
  await clearRememberedSelection();
  await kcLocalLogout();
}
